import { OrganizationRecordList, OrganizationType } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { type InputData, noddiAsync, type OrderingKeyOfUrlKey, URLKeys } from 'noddi-async';
import {
  ApiErrorPageInternal,
  BadgeVariant,
  filterUnitFor,
  NoddiBadge,
  NoddiColumnDef,
  NoddiLink,
  NoddiTable,
  useFilterSpecValue,
  useFilterUnits,
  useNoddiSortOrderParams,
  usePaginationQueryParam
} from 'noddi-ui-web';
import { pluralizeWithValue } from 'noddi-util';
import { type ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ADDRESS_FILTER_TEXT, useAddressOptions } from '../../hooks/filters/Address/useAddressFilter';
import { CITY_FILTER_TEXT, useCityOptions } from '../../hooks/filters/Location/useCitiesFilter';
import { ORGANIZATION_TYPE_FILTER_OPTIONS } from '../../hooks/filters/Organization/useOrganizationTypeFilter';
import {
  SERVICE_DEPARTMENT_FILTER_TEXT,
  useServiceDepartmentOptions
} from '../../hooks/filters/ServiceDepartment/useServiceDepartmentFilter';
import {
  SERVICE_ORGANIZATION_FILTER_TEXT,
  useServiceOrganizationOptions
} from '../../hooks/filters/ServiceOrganization/useServiceOrganizationFilter';
import { ServiceOrganizationMarks } from '../UserGroups/ServiceOrganizationMarks';
import { OrganizationRoutes } from './types';

type OrganizationOrderingKey = OrderingKeyOfUrlKey<typeof URLKeys.getPaginatedOrganizations>;
type OrganizationFilterInput = Omit<
  InputData<typeof URLKeys.getPaginatedOrganizations>,
  'pageIndex' | 'pageSize' | 'order' | 'search'
>;

const organizationFilterUnit = filterUnitFor<OrganizationFilterInput>();

const getOrganizationColumns = (
  includeServiceOrganizations: boolean,
  departmentsCell?: (organization: OrganizationRecordList) => ReactNode
): NoddiColumnDef<OrganizationRecordList, unknown, OrganizationOrderingKey>[] => {
  const columns: NoddiColumnDef<OrganizationRecordList, unknown, OrganizationOrderingKey>[] = [
    {
      // Own column so names line up whether or not the organization has a logo.
      header: () => <span className='sr-only'>Logo</span>,
      id: 'logo',
      size: 40,
      minSize: 40,
      maxSize: 40,
      cell: ({ row }) => {
        const url = row.original.logo?.url;
        if (!url) {
          return null;
        }
        return <img src={url} alt='' className='size-6 object-contain' />;
      }
    },
    {
      header: 'Name',
      accessorKey: 'name',
      meta: { orderingKey: 'name' }
    }
  ];

  if (includeServiceOrganizations) {
    columns.push({
      header: 'Service organizations',
      id: 'service-organizations',
      meta: { description: 'Service organizations this customer is connected to.' },
      cell: ({ row }) => <ServiceOrganizationMarks serviceOrganizations={row.original.serviceOrganizations ?? []} />
    });
  }

  columns.push(
    {
      header: 'Registration number',
      accessorKey: 'registrationNumber',
      cell: ({ row }) => {
        const regNumber = row.original.registrationNumber;
        if (!regNumber) {
          return null;
        }
        return regNumber.replace(/(\d{3})(?=\d)/g, '$1 ');
      }
    },
    {
      header: 'Type',
      accessorKey: 'type.label',
      cell: ({ row }) => (
        <NoddiBadge variant={getOrganizationTypeBadgeVariant(row.original.type.value)}>
          {row.original.type.label}
        </NoddiBadge>
      )
    },
    {
      header: 'Departments',
      accessorKey: 'numDepartments',
      cell: ({ row }) =>
        departmentsCell?.(row.original) ??
        pluralizeWithValue({ value: row.original.numDepartments, word: 'department' })
    }
  );

  return columns;
};

interface Props {
  routes: OrganizationRoutes;
  serviceDepartmentIds?: number[];
  serviceOrganizationIds?: number[];
  fillHeight?: boolean;
  /** Admin shows connected service organizations. Partner is already scoped to one. */
  includeServiceOrganizations?: boolean;
  departmentsCell?: (organization: OrganizationRecordList) => ReactNode;
  /** Where a row opens. The Customers list opens the customer; "Open organization" stays in the row menu. */
  rowTarget?: 'organization' | 'customer';
}

export const OrganizationsTable = ({
  routes,
  serviceDepartmentIds = [],
  serviceOrganizationIds = [],
  fillHeight = false,
  includeServiceOrganizations = false,
  departmentsCell,
  rowTarget = 'organization'
}: Props) => {
  const navigate = useNavigate();
  const { pagination, setPagination } = usePaginationQueryParam();
  const columns = useMemo(
    () => getOrganizationColumns(includeServiceOrganizations, departmentsCell),
    [departmentsCell, includeServiceOrganizations]
  );
  const { order, onOrderChange } = useNoddiSortOrderParams({ columns });
  // A caller that already scopes the list (partner's selected service organization) keeps that lock.
  // Admin leaves both empty, so the sidebar can filter the connection.
  const showServiceOrganizationFilter = serviceOrganizationIds.length === 0;
  const showServiceDepartmentFilter = serviceDepartmentIds.length === 0;
  const serviceOrganizationsFilter = organizationFilterUnit.multiSelect('serviceOrganizationIds', {
    ...SERVICE_ORGANIZATION_FILTER_TEXT,
    description: 'Show organizations connected to these service organizations.',
    source: useServiceOrganizationOptions({ enabled: showServiceOrganizationFilter }),
    exclude: true
  });
  const { serviceOrganizationIds: pickedServiceOrganizationIds } = useFilterSpecValue(serviceOrganizationsFilter);
  const serviceDepartmentsFilter = organizationFilterUnit.multiSelect('serviceDepartmentIds', {
    ...SERVICE_DEPARTMENT_FILTER_TEXT,
    description: 'Show organizations connected to these service departments, or with a booking there.',
    source: useServiceDepartmentOptions({
      serviceOrganizationIds: showServiceOrganizationFilter ? pickedServiceOrganizationIds : serviceOrganizationIds,
      enabled: showServiceDepartmentFilter
    }),
    exclude: true
  });
  const citySpec = organizationFilterUnit.multiSelect('cities', {
    ...CITY_FILTER_TEXT,
    source: useCityOptions(),
    exclude: true
  });
  const { cities } = useFilterSpecValue(citySpec);
  const filters = useFilterUnits([
    showServiceOrganizationFilter
      ? serviceOrganizationsFilter
      : organizationFilterUnit.locked({ serviceOrganizationIds }),
    showServiceDepartmentFilter ? serviceDepartmentsFilter : organizationFilterUnit.locked({ serviceDepartmentIds }),
    organizationFilterUnit.multiSelect('types', {
      title: 'Organization types',
      options: ORGANIZATION_TYPE_FILTER_OPTIONS,
      exclude: true
    }),
    citySpec,
    organizationFilterUnit.multiSelect('addressIds', {
      ...ADDRESS_FILTER_TEXT,
      source: useAddressOptions({ cities }),
      exclude: true
    })
  ]);
  const {
    data: organizations,
    isPending,
    isFetching,
    error: organizationsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedOrganizations,
    input: {
      ...pagination,
      ...filters.apiInput,
      order
    },
    queryConfig: {
      placeholderData: keepPreviousData
    }
  });

  if (organizationsError) {
    return <ApiErrorPageInternal error={organizationsError} />;
  }

  return (
    <NoddiTable
      headerNode={
        <NoddiLink variant='outline' startIcon='Building2' to={routes.departments.getPath()}>
          View departments
        </NoddiLink>
      }
      fillHeight={fillHeight}
      data={organizations?.results}
      setPagination={setPagination}
      pagination={pagination}
      totalCount={organizations?.count}
      isPending={isPending}
      isFetching={isFetching}
      getUrlPath={(item) => ({
        url:
          rowTarget === 'customer'
            ? routes.customerDetail.getPath({ id: item.userGroupId })
            : routes.editOrganization.getPath({ id: item.id })
      })}
      getRowId={(row) => row.id}
      columns={columns}
      ordering={order}
      onOrderChange={onOrderChange}
      filters={filters}
      searchPlaceholder='Search organizations'
      actions={[
        {
          label: 'Open organization',
          actionType: 'view',
          onClick: (row) => {
            navigate(routes.editOrganization.getPath({ id: row.id }));
          }
        }
      ]}
    />
  );
};

const getOrganizationTypeBadgeVariant = (type: OrganizationRecordList['type']['value']): BadgeVariant => {
  switch (type) {
    case OrganizationType.B2B_CUSTOMER:
      return 'default';
    case OrganizationType.INSURANCE_COMPANY:
      return 'info';
    case OrganizationType.MUNICIPALITY:
      return 'info';
    case OrganizationType.SUPPLIER:
      return 'warning';
    case OrganizationType.TIRE_HOTEL:
      return 'outline';
    case OrganizationType.OTHER:
    default:
      return 'outline';
  }
};
